// deps
import React, { useContext, useEffect, useMemo, useRef } from "react";

// internal-deps

import { isEmpty } from "@swiggy-private/js-utils";
import { Loader } from "@swiggy-private/dls-web-core/dist/components/Loader";
import {
    AnalyticsContext,
    ResponseContext,
    ScreenNames,
    TableContext,
    ObjectNames,
    useDigitalMenuConfig,
    IApiConfigs,
} from "@swiggy-private/portal-dineout-context";

// components
import BillErrorPage from "./BillErrorPage";
import BillPending from "./BillPending";
import BillSummary from "./BillSummary";
import { BILL_SUMMARY_PENDING_STATUS_CODE, BILL_SUMMARY_SUCCESS_STATUS_CODE } from "./constant";
import { OverLayLoader } from "./styles";

//types
import { BillSummaryPageProps } from "./types";
import { getV2ConfigFlags } from "../utils";

const BillSummaryPage = ({
    headers,
    sdk,
    clickAnalytics,
    impressionAnalytics,
    genericOnclick,
    onInvalidWidget,
    onWidgetError,
    onEmptyPage,
    startBillPolling,
    stopBillPolling,
    onErrorRetry,
    screenName,
}: BillSummaryPageProps) => {
    const { state } = useContext(ResponseContext);
    const { impressionEvent } = useContext(AnalyticsContext);
    const { restaurantId, tableId, parentOrderId } = useContext(TableContext);
    const pendingImpressionEvent = useRef(false);
    const { config } = useDigitalMenuConfig();
    const { isV2, isV2InApp } = useMemo(() => getV2ConfigFlags(config), [config]);

    const showOverlay = state.billSummary.loading;
    const statusCode = state.billSummary.data?.statusCode;
    const isError = state.billSummary.error;
    const popupConfigs = state.billSummary?.data?.success?.configs as IApiConfigs;
    useEffect(() => {
        if (!showOverlay) {
            if (statusCode === BILL_SUMMARY_PENDING_STATUS_CODE) {
                startBillPolling();
                return;
            } else {
                stopBillPolling();
            }
        }

        return () => {
            stopBillPolling?.();
        };
    }, [showOverlay, startBillPolling, statusCode, stopBillPolling]);

    useEffect(() => {
        if (showOverlay) return;

        if (
            statusCode === BILL_SUMMARY_SUCCESS_STATUS_CODE &&
            !isEmpty(state.billSummary?.data?.success?.cards)
        ) {
            impressionEvent({
                category: ScreenNames.BILL_SUMMARY,
                action: ObjectNames.IMPRESSION_BILL_SUMMARY_SUCCESS,
                context: JSON.stringify({
                    restaurant_id: restaurantId,
                    table_id: tableId,
                    parentOrderId,
                    table_no: state?.pin?.data?.tableNo,
                    is_v2_config: isV2,
                    theme: isV2InApp ? config?.themeInfo?.theme : undefined,
                }),
            });
        } else if (statusCode === BILL_SUMMARY_PENDING_STATUS_CODE) {
            if (pendingImpressionEvent.current) return;
            pendingImpressionEvent.current = true;
            impressionEvent({
                category: ScreenNames.BILL_SUMMARY,
                action: ObjectNames.IMPRESSION_BILL_SUMMARY_PENDING,
                context: JSON.stringify({
                    restaurant_id: restaurantId,
                    table_id: tableId,
                    parentOrderId,
                    table_no: state?.pin?.data?.tableNo,
                    is_v2_config: isV2,
                    theme: isV2InApp ? config?.themeInfo?.theme : undefined,
                }),
            });
        }
    }, [
        impressionEvent,
        parentOrderId,
        restaurantId,
        showOverlay,
        state.billSummary?.data?.success?.cards,
        statusCode,
        tableId,
        isV2,
        isV2InApp,
        config?.themeInfo?.theme,
        state?.pin?.data?.tableNo,
    ]);

    if (showOverlay) {
        return (
            <OverLayLoader>
                <Loader type="primary" size="large" />
            </OverLayLoader>
        );
    }

    if (isError) {
        return <BillErrorPage onErrorRetry={onErrorRetry} />;
    }

    if (statusCode === BILL_SUMMARY_PENDING_STATUS_CODE) {
        return (
            <BillPending
                sdk={sdk}
                headers={headers}
                clickAnalytics={clickAnalytics}
                impressionAnalytics={impressionAnalytics}
                genericOnclick={genericOnclick}
                onInvalidWidget={onInvalidWidget}
                onEmptyPage={onEmptyPage}
                onWidgetError={onWidgetError}
                screenName={screenName}
                popupConfigs={popupConfigs}
            />
        );
    }

    return (
        <BillSummary
            cards={state.billSummary?.data?.success?.cards || []}
            popupConfigs={popupConfigs}
            screenName={screenName}
            sdk={sdk}
            headers={headers}
            clickAnalytics={clickAnalytics}
            impressionAnalytics={impressionAnalytics}
            genericOnclick={genericOnclick}
            onInvalidWidget={onInvalidWidget}
            onEmptyPage={onEmptyPage}
            onWidgetError={onWidgetError}
        />
    );
};
export default React.memo(BillSummaryPage);
